import React, { useEffect, useRef, useState } from 'react'
import {
	BtnComponent,
	PhoneInput,
	UIDirection,
	Indent,
	RadioCardComponent,
	Heading,
	i18n,
	UITypes,
	FormNotificationComponent,
} from 'LibNeo'
import { Form, Row } from 'antd'
import { FieldNames } from 'Constants/FieldNames'
import { FormNames } from 'Constants/FormNames'
import { useFormConnector } from 'Src/hooks'
import { Access } from 'Constants/FunctionAliases'
import moment from 'moment'
import { authSelector } from 'Pages/auth'
import { useHasAccess } from 'Src/hooks/useAccess/useHasAccess'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'Src/hooks/useAppDispatch'
import { WrappedFormUtils } from 'antd/lib/form/Form'
import { Callback } from 'Src/types/utilTypes'
import {
	PaperLeadBg,
	DigitalLeadBg,
	bgLeadSlice,
	FlexWrapper,
	GrayCaption,
	BgAgreementBtnTranslate,
	isDisableButton,
	BgAgreementFaceType,
	BgAgreementTranslate,
	BgAgreementType,
	RequestType,
	SECOND_LIMIT_CONFIRM,
	AgreementCheckboxFieldNames,
	BankGuaranteeFull,
	AgreementCheckboxFields,
} from 'Pages/ibro'
import { PaperAgreementFormBlock } from './PaperAgreementFormBlock'
import { ElectronicAgreementFormBlock } from './ElectronicAgreementFormBlock'

const REQUIRED_FIELDS = {
	[BgAgreementType.CONFIRMATION]: [FieldNames.PHONE],
	[BgAgreementType.PAPER_LEAD]: [
		FieldNames.FIRST_NAME,
		FieldNames.LAST_NAME,
		FieldNames.IS_PAPER_AGREEMENT,
	],
	[BgAgreementType.ELECTRONIC]: [
		FieldNames.FIRST_NAME,
		FieldNames.LAST_NAME,
		AgreementCheckboxFieldNames.ELECTRONIC,
	],
}

const agreementTranslate = (Object.keys(BgAgreementTranslate) as BgAgreementType[]).map((type) => ({
	label: BgAgreementTranslate[type],
	value: type,
	key: type,
	description: i18n(`broker.tooltip_${type}`),
}))

interface ComponentProps {
	form: WrappedFormUtils
	itemSelector: Callback<unknown, Pick<BankGuaranteeFull, 'id' | 'confirmation_created_at' | 'inn'>>
	requestType?: RequestType
	createOptions?: { attorney: boolean }
	changeOptions?: { is_director: boolean }
}

const actions = {
	sendConfirmation: bgLeadSlice.actions.sendConfirmation,
	sendPaperLead: bgLeadSlice.actions.sendPaperLead,
	sendDigitalLead: bgLeadSlice.actions.sendDigitalLead,
}
const Agreement = ({
	form,
	itemSelector,
	requestType,
	createOptions,
	changeOptions,
}: ComponentProps) => {
	const { sendConfirmation, sendPaperLead, sendDigitalLead } = useAppDispatch(actions)
	const item = useSelector(itemSelector)
	const isDigitalConfirmationsHasIbro = useSelector(authSelector.isDigitalConfirmationsHasIbro)
	const timer: React.MutableRefObject<NodeJS.Timeout | null> = useRef(null)
	const [agreementType, setAgreementType] = useState(BgAgreementType.CONFIRMATION)
	const [isCredentials, changeIsCredentials] = useState(true)
	const [timerSecond, setTimerSecond] = useState(0)
	const hasAccess = useHasAccess()
	const { resetFields } = form

	const changeAgreementFace = (value: BgAgreementFaceType) => {
		changeIsCredentials(value === BgAgreementFaceType.LEADER)
	}
	const changeAgreementType = (value: BgAgreementType) => {
		setAgreementType(value)
		changeAgreementFace(BgAgreementFaceType.LEADER)
		resetFields([
			FieldNames.FIRST_NAME,
			FieldNames.LAST_NAME,
			FieldNames.EMAIL,
			FieldNames.IS_PAPER_AGREEMENT,
			FieldNames.MIDDLE_NAME,
		])
	}

	const optionsAgreementType = agreementTranslate.filter(
		(option) => isDigitalConfirmationsHasIbro || option.key !== BgAgreementType.ELECTRONIC
	)
	useFormConnector(FormNames.BROKER.AGREEMENT, form)

	const disableAll = !isCredentials

	const startTimer = (sec: number) => {
		if (timer.current) clearTimeout(timer.current)
		const beginTimer = (seconds: number) => {
			if (seconds > 1) {
				setTimerSecond(seconds - 1)
				if (timer.current) clearTimeout(timer.current)
				timer.current = setTimeout(beginTimer, 1000, seconds - 1)
			} else {
				setTimerSecond(0)
			}
		}
		timer.current = setTimeout(beginTimer, 1000, sec)
	}

	useEffect(() => {
		const { confirmation_created_at } = item
		if (confirmation_created_at) {
			const second = moment(new Date()).diff(moment(confirmation_created_at), 'seconds')
			if (second <= SECOND_LIMIT_CONFIRM) startTimer(SECOND_LIMIT_CONFIRM - second)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [item?.confirmation_created_at])

	const handlerClick = () => {
		const values = form.getFieldsValue()
		const filteredValues = Object.keys(values).reduce(
			(accum, key) =>
				AgreementCheckboxFields.includes(key) ? accum : { ...accum, [key]: values[key] },
			{}
		)
		const { middle_name } = values
		const empty_middle_name = middle_name?.trim().length === 0
		if (agreementType === BgAgreementType.CONFIRMATION) {
			return sendConfirmation({
				id: item.id,
				phone: values.phone,
				requestType,
				...createOptions,
				...changeOptions,
			})
		}
		if (agreementType === BgAgreementType.PAPER_LEAD) {
			return sendPaperLead({
				requestType,
				id: item.id,
				data: Object.assign(new PaperLeadBg(), {
					...filteredValues,
					empty_middle_name,
					middle_name: empty_middle_name ? '' : middle_name,
					...changeOptions,
					...createOptions,
				}),
			})
		}
		if (agreementType === BgAgreementType.ELECTRONIC) {
			return sendDigitalLead({
				requestType,
				id: item.id,
				data: Object.assign(new DigitalLeadBg(), {
					...filteredValues,
					empty_middle_name,
					middle_name: empty_middle_name ? '' : middle_name,
					...changeOptions,
					...createOptions,
				}),
			})
		}
	}

	const onlySeconds = timerSecond % 60

	const disabledPaperAgree = !form.getFieldValue(FieldNames.IS_PAPER_AGREEMENT)

	const disableSubmit =
		!hasAccess([Access.broker.update]) ||
		disableAll ||
		(disabledPaperAgree && agreementType === BgAgreementType.PAPER_LEAD) ||
		(timerSecond > 0 && agreementType === BgAgreementType.CONFIRMATION) ||
		isDisableButton(REQUIRED_FIELDS[agreementType], form.getFieldsValue(), form.getFieldsError())

	return (
		<>
			<Heading type="h5">Получение согласия</Heading>
			<Indent top={24}>
				<FormNotificationComponent
					hideIcon
					type={UITypes.PASSIVE}
					message={`ИНН клиента\n${item.inn}`}
				/>
			</Indent>

			<Indent top={16}>
				<RadioCardComponent
					options={optionsAgreementType}
					value={agreementType}
					onChange={changeAgreementType}
					direction={UIDirection.HORIZONTAL}
				/>
			</Indent>
			<Indent top={16}>
				{agreementType === BgAgreementType.CONFIRMATION && (
					<PhoneInput
						form={form}
						fieldName={FieldNames.PHONE}
						label="broker.phone"
						disabled={disableAll}
					/>
				)}
				{agreementType === BgAgreementType.PAPER_LEAD && (
					<PaperAgreementFormBlock form={form} disableAll={disableAll} />
				)}
				{agreementType === BgAgreementType.ELECTRONIC && (
					<ElectronicAgreementFormBlock form={form} disableAll={disableAll} />
				)}
			</Indent>
			<Indent top={24}>
				<FlexWrapper>
					<Row type="flex" align="middle">
						<BtnComponent
							title={`broker.${BgAgreementBtnTranslate[agreementType]}`}
							onClick={handlerClick}
							disabled={disableSubmit}
						/>
						{timerSecond > 0 && agreementType === BgAgreementType.CONFIRMATION && (
							<Indent left={10}>
								<GrayCaption>
									Отправить повторно можно через &nbsp;
									{(timerSecond - onlySeconds) / 60}:
									{onlySeconds > 9 ? onlySeconds : `0${onlySeconds}`}
								</GrayCaption>
							</Indent>
						)}
					</Row>
				</FlexWrapper>
			</Indent>
		</>
	)
}

export const AgreementModal = Form.create({
	name: FormNames.BROKER.AGREEMENT,
})(Agreement)
